import { FC, MouseEvent, useRef, useState } from 'react'
import {
  Text,
  GreyGrey40,
  GreyGrey60,
  GreyGrey80,
  Primary12Regular,
  Primary12SemiBold,
  RedRed80,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  GRAPH_HEIGHT,
  GRAPH_WIDTH,
  GRAPH_WINDOW_MS
} from './performanceMonitorBar.constants'
import { MetricSample } from './performanceMonitorBar.types'

type MetricGraphProps = {
  history: MetricSample[]
  yMax: number
  threshold: number | null
  accentColor: string
  borderColor: string
  isOverThreshold: boolean
  formatValue: (value: number) => string
}

type GraphPoint = {
  x: number
  y: number
  sample: MetricSample
}

const formatTimeOfDay = (timestamp: number) => {
  const date = new Date(timestamp)
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

export const MetricGraph: FC<MetricGraphProps> = ({
  history,
  yMax,
  threshold,
  accentColor,
  borderColor,
  isOverThreshold,
  formatValue
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const latestSampleTimestamp =
    history.length > 0 ? history[history.length - 1].timestamp : Date.now()
  const windowStart = latestSampleTimestamp - GRAPH_WINDOW_MS
  const safeMax = Math.max(yMax, 1)

  const points: GraphPoint[] = history.map(sample => {
    const x = ((sample.timestamp - windowStart) / GRAPH_WINDOW_MS) * GRAPH_WIDTH
    const y = GRAPH_HEIGHT - (sample.value / safeMax) * GRAPH_HEIGHT
    return { x, y, sample }
  })

  const firstPoint = points[0] ?? null
  const lastPoint = points[points.length - 1] ?? null

  const polyline = points.map(p => `${p.x},${p.y}`).join(' ')

  const areaPath =
    firstPoint !== null && lastPoint !== null
      ? `M 0 ${GRAPH_HEIGHT} L 0 ${firstPoint.y} L ${points
          .map(p => `${p.x} ${p.y}`)
          .join(' L ')} L ${lastPoint.x} ${GRAPH_HEIGHT} Z`
      : ''

  const preHistoryLine =
    firstPoint !== null && firstPoint.x > 0
      ? { x1: 0, y1: firstPoint.y, x2: firstPoint.x, y2: firstPoint.y }
      : null

  const thresholdY =
    threshold !== null
      ? GRAPH_HEIGHT - (threshold / safeMax) * GRAPH_HEIGHT
      : null

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>) => {
    if (containerRef.current === null || points.length === 0) return
    const rect = containerRef.current.getBoundingClientRect()
    const relativeX = event.clientX - rect.left
    const svgX = (relativeX / rect.width) * GRAPH_WIDTH
    const nearest = points.reduce<{ index: number; distance: number }>(
      (best, point, index) => {
        const distance = Math.abs(point.x - svgX)
        if (distance < best.distance) return { index, distance }
        return best
      },
      { index: 0, distance: Number.POSITIVE_INFINITY }
    )
    setHoveredIndex(nearest.index)
  }

  const handleMouseLeave = () => {
    setHoveredIndex(null)
  }

  const hoveredPoint =
    hoveredIndex !== null && points[hoveredIndex] !== undefined
      ? points[hoveredIndex]
      : null

  const hoveredLeftPercent =
    hoveredPoint !== null ? (hoveredPoint.x / GRAPH_WIDTH) * 100 : 0
  const tooltipOnLeft = hoveredLeftPercent > 60

  return (
    <Box width="100%">
      <Box
        ref={containerRef}
        position="relative"
        width="100%"
        height={`${GRAPH_HEIGHT}px`}
        border={`1px solid ${borderColor}`}
        borderRadius="4px"
        backgroundColor="white"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        cursor={points.length > 0 ? 'crosshair' : undefined}
        overflow="hidden"
      >
        <svg
          width="100%"
          height="100%"
          viewBox={`0 0 ${GRAPH_WIDTH} ${GRAPH_HEIGHT}`}
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          {thresholdY !== null &&
            thresholdY >= 0 &&
            thresholdY <= GRAPH_HEIGHT && (
              <line
                x1={0}
                x2={GRAPH_WIDTH}
                y1={thresholdY}
                y2={thresholdY}
                stroke={RedRed80}
                strokeWidth={1}
                strokeDasharray="2 2"
                vectorEffect="non-scaling-stroke"
              />
            )}
          {areaPath !== '' && (
            <path d={areaPath} fill={accentColor} fillOpacity={0.15} />
          )}
          {preHistoryLine !== null && (
            <line
              x1={preHistoryLine.x1}
              x2={preHistoryLine.x2}
              y1={preHistoryLine.y1}
              y2={preHistoryLine.y2}
              stroke={isOverThreshold ? RedRed80 : accentColor}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
          )}
          {polyline !== '' && (
            <polyline
              points={polyline}
              fill="none"
              stroke={isOverThreshold ? RedRed80 : accentColor}
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
          )}
          {hoveredPoint !== null && (
            <>
              <line
                x1={hoveredPoint.x}
                x2={hoveredPoint.x}
                y1={0}
                y2={GRAPH_HEIGHT}
                stroke={GreyGrey60}
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
              <circle
                cx={hoveredPoint.x}
                cy={hoveredPoint.y}
                r={3}
                fill={isOverThreshold ? RedRed80 : accentColor}
                stroke="white"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            </>
          )}
        </svg>
        {hoveredPoint !== null && (
          <Box
            position="absolute"
            top="4px"
            transform={tooltipOnLeft ? 'translateX(-8px)' : 'translateX(8px)'}
            backgroundColor={GreyGrey80}
            color="white"
            paddingX="6px"
            paddingY="4px"
            borderRadius="3px"
            pointerEvents="none"
            whiteSpace="nowrap"
            zIndex={1}
            style={
              tooltipOnLeft
                ? { right: `${100 - hoveredLeftPercent}%` }
                : { left: `${hoveredLeftPercent}%` }
            }
          >
            <Text {...Primary12SemiBold} color="white">
              {formatValue(hoveredPoint.sample.value)}
            </Text>
            <Text {...Primary12Regular} color="white">
              {formatTimeOfDay(hoveredPoint.sample.timestamp)}
            </Text>
          </Box>
        )}
        <Box position="absolute" top="2px" right="4px" pointerEvents="none">
          <Text {...Primary12Regular} color={GreyGrey40}>
            {formatValue(yMax)}
          </Text>
        </Box>
      </Box>
      <Flex justifyContent="space-between" paddingTop="2px">
        <Text {...Primary12Regular} color={GreyGrey40}>
          {formatTimeOfDay(windowStart)}
        </Text>
        <Text {...Primary12Regular} color={GreyGrey40}>
          now
        </Text>
      </Flex>
    </Box>
  )
}
